/**
 * PostContent - Post text and media display. Phase 4: hashtags, location, aura pills.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AuraPill from './AuraPill';
import ImageLightbox from '../../modals/ImageLightbox';

/**
 * Normalize stored post/comment text for display. Strips replacement / control chars that
 * WebKit often renders as a blue "unknown character" glyph on iOS after line breaks or bad imports.
 */
export function normalizePostContentForDisplay(content) {
  if (content == null || typeof content !== 'string') return content;
  return content
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\uFEFF/g, '')
    .replace(/\uFFFD/g, '')
    .replace(/\uFFFC/g, '');
}

/** Split content into text, mention, hashtag, and location segments for rendering links. Used by posts and comments. */
export function parseContentWithHashtagsMentionsAndLocation(content, mentions = [], location = null) {
  if (!content) return [];
  const normalized = normalizePostContentForDisplay(content);
  content = normalized;
  const mentionMap = new Map((mentions || []).map((m) => [m.username?.toLowerCase(), m]));
  // Location: stop at 2+ spaces so "📍 Waverly Place   nice" parses as location + text, not location including "nice".
  // Stop at newlines so multi-line post/comment bodies after a 📍 pin are not swallowed into the location.
  // Allow zero or more spaces after 📍 so "@user📍Brooklyn Bowl" and "📍Brooklyn" match, not only "📍 Brooklyn".
  const combined = /(@[a-zA-Z0-9_]+|#[a-zA-Z0-9_]+|📍[^\S\n]*[^\n@#]+?(?=\s{2,}|\n|@|#|$))/g;
  const segments = [];
  let lastIndex = 0;
  let match;
  while ((match = combined.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: content.slice(lastIndex, match.index) });
    }
    const raw = match[0];
    if (raw.startsWith('@')) {
      const username = raw.slice(1);
      segments.push({ type: 'mention', value: username, user: mentionMap.get(username?.toLowerCase()) });
    } else if (raw.startsWith('#')) {
      segments.push({ type: 'hashtag', value: raw.slice(1) });
    } else if (raw.startsWith('📍')) {
      const placeName = raw.replace(/^📍\s*/, '').trim();
      const loc = location && location.place_name === placeName ? location : null;
      segments.push({ type: 'location', value: placeName, location: loc });
    }
    lastIndex = combined.lastIndex;
  }
  if (lastIndex < content.length) {
    segments.push({ type: 'text', value: content.slice(lastIndex) });
  }
  return segments;
}

function PostContent({ post, stopPropagationOnLinks = false }) {
  const media = post.media || [];
  const location = post.location;
  const mentions = post.mentions || [];
  const hashtags = post.hashtags || [];
  const events = post.events || [];
  const content = post.content;
  const [hiddenMediaIndices, setHiddenMediaIndices] = useState(() => new Set());
  const [lightboxSrc, setLightboxSrc] = useState(null);

  useEffect(() => {
    setHiddenMediaIndices(new Set());
    setLightboxSrc(null);
  }, [post?.post_id]);

  const hideMediaAt = (index) => {
    setHiddenMediaIndices((prev) => {
      if (prev.has(index)) return prev;
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  };

  const hasVisibleMedia = media.length > 0 && media.some((_, i) => !hiddenMediaIndices.has(i));

  const segments = parseContentWithHashtagsMentionsAndLocation(content, mentions, location);
  const hashtagMap = new Map(hashtags.map((h) => [h.hashtag?.toLowerCase(), h]));
  const linkProps = stopPropagationOnLinks ? { onClick: (e) => e.stopPropagation() } : {};

  return (
    <div className="flex flex-col gap-4">
      {post.content && (
        <p className="text-ash whitespace-pre-wrap break-words">
          {segments.map((seg, i) =>
            seg.type === 'mention' ? (
              <span key={i} className="inline-flex items-center gap-1">
                <Link
                  to={`/profile/${encodeURIComponent(seg.value)}`}
                  className="text-ether hover:underline"
                  {...linkProps}
                >
                  @{seg.value}
                </Link>
                {seg.user && (
                  <span {...(stopPropagationOnLinks && { onClick: (e) => e.stopPropagation() })}>
                    <AuraPill
                      targetId={seg.user.user_id}
                      targetType="user"
                      postId={post.post_id}
                      auraCount={seg.user.aura_count ?? 0}
                    />
                  </span>
                )}
              </span>
            ) : seg.type === 'hashtag' ? (
              <span key={i} className="inline-flex items-center gap-1">
                <Link
                  to={`/?hashtag=${encodeURIComponent(seg.value)}`}
                  className="text-ether hover:underline"
                  {...linkProps}
                >
                  #{seg.value}
                </Link>
                {hashtagMap.get(seg.value?.toLowerCase()) && (
                  <span {...(stopPropagationOnLinks && { onClick: (e) => e.stopPropagation() })}>
                    <AuraPill
                      targetId={hashtagMap.get(seg.value?.toLowerCase()).hashtag_id}
                      targetType="hashtag"
                      postId={post.post_id}
                      auraCount={hashtagMap.get(seg.value?.toLowerCase()).aura_count ?? 0}
                    />
                  </span>
                )}
              </span>
            ) : seg.type === 'location' ? (
              <span key={i} className="inline-flex items-center gap-1">
                {seg.location?.location_id ? (
                  <Link
                    to={`/?location_id=${seg.location.location_id}`}
                    className="text-mist hover:underline"
                    {...linkProps}
                  >
                    📍 {seg.value}
                  </Link>
                ) : (
                  <span className="text-mist">📍 {seg.value}</span>
                )}
                {seg.location?.location_id && (
                  <span {...(stopPropagationOnLinks && { onClick: (e) => e.stopPropagation() })}>
                    <AuraPill
                      targetId={seg.location.location_id}
                      targetType="location"
                      postId={post.post_id}
                      auraCount={seg.location.aura_count ?? 0}
                    />
                  </span>
                )}
              </span>
            ) : (
              <span key={i}>{seg.value}</span>
            )
          )}
        </p>
      )}
      {events.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {events.map((ev) => (
            <Link
              key={ev.event_id}
              to={`/events/${ev.event_id}`}
              className="inline-flex items-center px-2 py-1 rounded-lg bg-mist/20 text-mist text-sm hover:bg-mist/30"
              {...linkProps}
            >
              {ev.name}
            </Link>
          ))}
        </div>
      )}
      {hasVisibleMedia && (
        <div className="flex flex-wrap gap-2">
          {media.map((m, i) => {
            if (hiddenMediaIndices.has(i)) return null;
            return (
              <div key={i} className="rounded-xl overflow-hidden max-w-full">
                {m.media_type === 'video' ? (
                  <video
                    src={m.media_url}
                    controls
                    className="max-h-64"
                    onError={() => hideMediaAt(i)}
                  />
                ) : (
                  <button
                    type="button"
                    className="block max-w-full cursor-zoom-in border-0 bg-transparent p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxSrc(m.media_url);
                    }}
                    aria-label="View photo"
                  >
                    <img
                      src={m.media_url}
                      alt=""
                      className="max-h-64 object-cover rounded-xl"
                      onError={() => hideMediaAt(i)}
                    />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {lightboxSrc && (
        <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
      )}
    </div>
  );
}

export default PostContent;
